/* original source: https://tremor.so/docs/ui/badge */
import { Slot } from './Slot'
import { tv } from '@joyful/utils'
import { cloneElement, isValidElement } from 'react'
import type { ComponentProps, ReactElement, ReactNode } from 'react'

import type { VariantProps } from '@joyful/utils'

export const badgeVariants = tv({
  base: 'inline-flex w-fit max-w-full items-center gap-x-1 ring-1 transition-colors ring-inset [[type=button]]:not-aria-disabled:cursor-pointer aria-disabled:cursor-not-allowed aria-disabled:opacity-50',

  compoundVariants: [
    {
      class: {
        base: 'rounded-sm',
      },
      rounding: 'md',
      size: ['icon', 'xxs', 'xs'],
    },
    {
      class: {
        base: 'rounded-md',
      },
      rounding: 'md',
      size: ['sm', 'md', 'lg'],
    },
  ],

  defaultVariants: {
    overflowBehavior: 'none',
    rounding: 'md',
    size: 'md',
    variant: 'neutral',
    weight: 'medium',
  },

  slots: {
    content: null,
  },

  variants: {
    borderVariant: {
      dashed: 'border border-dashed border-tremor-border ring-0',
    },

    overflowBehavior: {
      none: {
        content: 'whitespace-nowrap',
      },
      truncate: {
        base: 'min-w-0',
        content: 'min-w-0 flex-[1_0_0] truncate',
      },
      wrap: {
        content: 'text-start text-balance wrap-break-word whitespace-normal',
      },
    },

    rounding: {
      full: {
        base: 'rounded-full',
      },
      md: {
        base: null,
      },
    },

    size: {
      icon: {
        base: 'size-5 justify-center p-0 text-xs tabular-nums',
        content: 'flex items-center justify-center',
      },
      lg: {
        base: 'px-2.5 py-0.5 text-base',
      },
      md: {
        base: 'px-2.5 py-0.5 text-xs md:text-sm',
      },
      sm: {
        base: 'px-2 py-0.5 text-xs',
      },
      xs: {
        base: 'px-2 py-0.5 text-xs',
      },
      xxs: {
        base: 'px-1.5 py-0.5 text-[0.6rem] leading-4',
      },
    },

    variant: {
      brand: {
        base: 'bg-yellow-100 text-yellow-600 ring-yellow-500 dark:bg-yellow-900/30 dark:text-yellow-400 dark:ring-yellow-600',
      },
      error: {
        base: 'bg-red-50 text-red-900 ring-red-600/20 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-600/30',
      },
      info: {
        base: 'bg-blue-500/10 text-blue-600 ring-blue-500/30 dark:bg-blue-900/30 dark:text-blue-400',
      },
      neutral: {
        base: 'bg-gray-100 text-tremor-content-emphasis ring-gray-500/30 dark:bg-tremor-background-subtle dark:ring-tremor-border',
      },
      purple: {
        base: 'bg-purple-50 text-purple-900 ring-purple-500/30 dark:bg-purple-900/30 dark:text-purple-400',
      },
      success: {
        base: 'bg-emerald-50 text-emerald-900 ring-emerald-600/30 dark:bg-emerald-900/30 dark:text-emerald-400',
      },
      warning: {
        base: 'bg-yellow-50 text-yellow-900 ring-yellow-600/30 dark:bg-yellow-900/30 dark:text-yellow-400',
      },
    },

    weight: {
      light: 'font-light',
      medium: 'font-medium',
      mono: 'font-mono',
      normal: 'font-normal',
    },
  },
})

export interface BadgeProps
  extends
    Omit<ComponentProps<'div'>, 'className'>,
    VariantProps<typeof badgeVariants> {
  /**
   * @default false
   */
  asChild?: boolean
  endAdornment?: ReactElement<unknown>
  startAdornment?: ReactElement<unknown>
}

export function Badge({
  asChild = false,
  borderVariant,
  children,
  endAdornment,
  overflowBehavior,
  rounding,
  size,
  startAdornment,
  variant,
  weight,
  ...props
}: BadgeProps) {
  const Component = asChild ? Slot : 'span'

  const classNames = badgeVariants({
    borderVariant,
    overflowBehavior,
    rounding,
    size,
    variant,
    weight,
  })

  return (
    <Component className={classNames.base()} {...props}>
      {asChild ? (
        isValidElement<{ children: ReactNode }>(children) ? (
          // oxlint-disable-next-line react/no-clone-element
          cloneElement(
            children,
            undefined,
            startAdornment,
            <span className={classNames.content()}>
              {children.props.children}
            </span>,
            endAdornment,
          )
        ) : null
      ) : (
        <>
          {startAdornment}
          <span className={classNames.content()}>{children}</span>
          {endAdornment}
        </>
      )}
    </Component>
  )
}
